import React, { useState } from 'react';
import { FormattedMessage } from '../../../util/reactIntl';
import { NamedLink } from '../../../components';

import css from './NoSearchResultsMaybe.module.css';

/**
 * Where the waitlist signup is sent. Any endpoint that accepts a JSON POST
 * works — a Zapier catch hook, a Formspree form, or your own /api route.
 *
 * If it is not set, the form is not shown at all. Never tell someone they are
 * on a list that does not exist: an address we cannot store is a promise we
 * cannot keep.
 */
const WAITLIST_ENDPOINT = process.env.REACT_APP_WAITLIST_ENDPOINT || '';

/**
 * Optional token sent with each submission, matched by the endpoint. It is
 * compiled into the public bundle, so anyone determined can read it — it deters
 * casual abuse of an open endpoint, nothing more. Leave unset if you prefer.
 */
const WAITLIST_SECRET = process.env.REACT_APP_WAITLIST_SECRET || '';

/** Where to point people when the waitlist cannot take their address. */
const CONTACT_EMAIL = 'hello@drivelo.com.au';

/**
 * Query parameters that describe where and how to show results rather than
 * narrowing them. Anything else in the URL is a filter the visitor chose.
 * Tracking tags (utm_*, gclid, fbclid) are not filters either.
 */
const NON_FILTER_KEYS = ['address', 'bounds', 'origin', 'mapSearch', 'page', 'sort'];
const isTrackingKey = key => /^utm_/.test(key) || ['gclid', 'fbclid', 'msclkid'].includes(key);

const searchParams = search => {
  try {
    return new URLSearchParams(search || '');
  } catch (e) {
    return new URLSearchParams('');
  }
};

/**
 * Did the endpoint actually store the submission?
 *
 * A 200 is not enough. A misconfigured Google Apps Script web app answers with
 * HTTP 200 and an HTML error page — "Script function not found: doPost" was the
 * real response during setup — and an undeployed change or a sign-in
 * interstitial looks the same. Trusting the status alone would tell someone
 * they are on the list when nothing was written.
 *
 * So: a JSON body that does not say failure counts as stored; anything that
 * smells of an HTML page does not. Apps Script, Zapier and Formspree all answer
 * with JSON on success.
 */
const wasStored = async response => {
  if (!response.ok) {
    return false;
  }
  let body = '';
  try {
    body = (await response.text()).trim();
  } catch (e) {
    // An opaque or unreadable body: fall back to the status alone.
    return true;
  }
  if (body.startsWith('<')) {
    return false;
  }
  try {
    const parsed = JSON.parse(body);
    return parsed?.ok !== false && parsed?.error == null && parsed?.success !== false;
  } catch (e) {
    // Not JSON and not HTML — e.g. a bare "OK". Take the 2xx at face value.
    return true;
  }
};

const NoSearchResultsMaybe = props => {
  const { listingsAreLoaded, totalItems, location, resetAll, showCreateListingsLink } = props;
  const [suburb, setSuburb] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error | failed
  const hasWaitlist = !!WAITLIST_ENDPOINT;

  const hasNoResult = listingsAreLoaded && totalItems === 0;
  const params = searchParams(location.search);
  // When a filter emptied the results, the suburb is not the reason — saying
  // "we're not in your suburb yet" would send a learner away from an area we
  // may well cover. Measured on the live site: the price filter's default
  // range stopped below the one listing's price and produced exactly that.
  const hasActiveFilters = [...params.keys()].some(
    key => !NON_FILTER_KEYS.includes(key) && !isTrackingKey(key)
  );
  const searchedAddress = (params.get('address') || '').split(',')[0].trim();
  const mailtoHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
    'Tell me when Drivelo reaches my suburb'
  )}&body=${encodeURIComponent(`My suburb: ${searchedAddress}\n`)}`;

  if (!hasNoResult) {
    return null;
  }

  const handleSubmit = async e => {
    e.preventDefault();
    if (!suburb.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    const payload = {
      suburb: suburb.trim(),
      email: email.trim(),
      search: location.search,
      ...(WAITLIST_SECRET ? { secret: WAITLIST_SECRET } : {}),
    };
    try {
      const response = await fetch(WAITLIST_ENDPOINT, {
        method: 'POST',
        // text/plain on purpose. The body is still JSON, but this keeps the
        // request "simple" so the browser sends no CORS preflight. A Google
        // Apps Script web app — the endpoint this is built for — does not
        // answer preflight (OPTIONS) requests, so an application/json header
        // fails before the POST is made. Zapier and Formspree accept this too.
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
      });
      setStatus((await wasStored(response)) ? 'done' : 'failed');
    } catch (err) {
      setStatus('failed');
    }
  };

  if (status === 'done') {
    return (
      <div className={css.waitlist}>
        <h2 className={css.waitlistHeading}>You're on the list.</h2>
        <p className={css.waitlistBody}>
          We'll email you the moment a verified instructor covers {suburb.trim()}. In the meantime,
          nothing else to do.
        </p>
      </div>
    );
  }

  // The submission did not reach us. Say so, and give a way through that does
  // not depend on the thing that just failed.
  if (status === 'failed') {
    return (
      <div className={css.waitlist}>
        <h2 className={css.waitlistHeading}>That didn't save — sorry.</h2>
        <p className={css.waitlistBody}>
          Something went wrong at our end, so you are <strong>not</strong> on the list. Email us at{' '}
          <a className={css.secondaryLink} href={`mailto:${CONTACT_EMAIL}`}>
            {CONTACT_EMAIL}
          </a>{' '}
          with your suburb and we'll add you by hand.
        </p>
        <button className={css.submit} type="button" onClick={() => setStatus('idle')}>
          Try again
        </button>
      </div>
    );
  }

  if (hasActiveFilters) {
    return (
      <div className={css.waitlist}>
        <h2 className={css.waitlistHeading}>No instructors match these filters.</h2>
        <p className={css.waitlistBody}>
          Try removing a filter to see every instructor who teaches in this area.
        </p>
        <button className={css.submit} type="button" onClick={e => resetAll(e)}>
          <FormattedMessage id={'SearchPage.resetAllFilters'} />
        </button>
      </div>
    );
  }

  return (
    <div className={css.waitlist}>
      <h2 className={css.waitlistHeading}>We're not in your suburb yet — but we're close.</h2>
      <p className={css.waitlistBody}>
        Drivelo is opening suburb by suburb across Australia.{' '}
        {hasWaitlist
          ? "Tell us where you are and we'll email you the moment a verified instructor goes live near you. No spam, no obligation."
          : `Email us at ${CONTACT_EMAIL} with your suburb and we'll let you know the moment a verified instructor goes live near you.`}
      </p>

      {/* No endpoint means nowhere to store an address, so the form is not
          offered at all rather than collecting something we would drop. */}
      {hasWaitlist ? (
        <form className={css.waitlistForm} onSubmit={handleSubmit}>
          <label className={css.label} htmlFor="waitlist-suburb">
            Suburb or postcode
          </label>
          <input
            id="waitlist-suburb"
            className={css.input}
            type="text"
            value={suburb}
            onChange={e => setSuburb(e.target.value)}
            placeholder="Brunswick, 3056"
            autoComplete="address-level2"
          />

          <label className={css.label} htmlFor="waitlist-email">
            Email
          </label>
          <input
            id="waitlist-email"
            className={css.input}
            type="email"
            inputMode="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
          />

          {status === 'error' ? (
            <p className={css.error}>Enter your suburb and a valid email address.</p>
          ) : null}

          <button className={css.submit} type="submit" disabled={status === 'sending'}>
            {status === 'sending' ? 'Adding you…' : "Tell me when you're live"}
          </button>
        </form>
      ) : (
        <p className={css.waitlistBody}>
          <a className={css.secondaryLink} href={mailtoHref}>
            Email {CONTACT_EMAIL}
          </a>
        </p>
      )}

      <p className={css.secondary}>
        Are you an instructor?{' '}
        <NamedLink
          className={css.secondaryLink}
          name="SignupForUserTypePage"
          params={{ userType: 'provider' }}
        >
          Apply to join Drivelo
        </NamedLink>
      </p>

      {showCreateListingsLink ? (
        <p>
          <NamedLink className={css.createListingLink} name="NewListingPage">
            <FormattedMessage id="SearchPage.createListing" />
          </NamedLink>
        </p>
      ) : null}
    </div>
  );
};

export default NoSearchResultsMaybe;
